<script setup lang="ts">
import { computed } from 'vue';
import type { SelectedTransactionMessage as SelectedTransactionMessageType } from '@/common/bot/types/messages';
import type { TransStatement, BetHistoryEntity } from '@/common/api/model';
import { getTransactionId } from '@/common/transaction-utils';
import ChatMessageWrapper from '../ChatMessageWrapper.vue';
import TransactionItem from './TransactionItem.vue';
import EditPencil from './icons/EditPencil.vue';
import { buildExternalEvent } from '@/common/bot/lib/event.factory';
import { ExternalBotEvent } from '@/common/bot/enum/ExternalBotEvent';
import type { UserEditSelectedTransactionEvent } from '@/common/bot/types/events/external/external-events';
import { useUserSelectedTransactionCardCustomisation } from './composables/useTransactionItemCustomisation';
import * as SUtils from '@/common/customisation/utils/styleUtils';

export interface SelectedTransactionMessageProps {
  /**
   * The message to render.
   */
  message: SelectedTransactionMessageType;
  /**
   * The previous message/notice in the stack.
   */
  previous?: unknown;
  /**
   * The next message/notice in the stack.
   */
  next?: unknown;
}

const props = defineProps<SelectedTransactionMessageProps>();

const emit = defineEmits<{
  (e: 'message:input', event: UserEditSelectedTransactionEvent): void;
  (e: 'external-event', event: UserEditSelectedTransactionEvent): void;
}>();

// Create a summary message based on selected transactions
const summaryMessage = computed(() => {
  if (props.message.message) return props.message.message;

  const count = props.message.transactions.length;
  if (count === 0) return 'Transaction not listed';
  if (count === 1) return `Selected ${count} transaction`;
  return `Selected ${count} transactions`;
});

// Check if showing multiple transactions
const hasTransactions = computed(() => props.message.transactions.length > 0);
// Editable only when allowed and not yet edited (one edit per message)
const isEditable = computed(() => (props.message.isEditable ?? false) && !(props.message.wasEdited ?? false));

const handleEdit = (transaction?: TransStatement | BetHistoryEntity) => {
  const editEvent = buildExternalEvent<UserEditSelectedTransactionEvent>({
    type: ExternalBotEvent.USER_EDIT_SELECTED_TRANSACTION,
    payload: {
      ...(transaction !== undefined && { transaction }),
      message: props.message,
    },
  });

  emit('external-event', editEvent);
};

const { actionIconColor } = useUserSelectedTransactionCardCustomisation();
const actionIconStyle = computed(() => SUtils.createTextStyle(actionIconColor));
</script>

<template>
  <ChatMessageWrapper
    :previous="previous"
    :next="next"
    :message="{ ...message, message: summaryMessage }"
    :render-bubble="!hasTransactions"
  >
    <!-- Prepend: edit pencil outside bubble (for "Transaction not listed" only) -->
    <template v-if="!hasTransactions && isEditable" #prepend>
      <div
        class="transaction-edit-icon"
        :style="actionIconStyle"
        role="button"
        tabindex="0"
        aria-label="Edit transaction selection"
        @click="() => handleEdit()"
        @keydown.enter="() => handleEdit()"
        @keydown.space.prevent="() => handleEdit()"
      >
        <EditPencil />
      </div>
    </template>

    <!-- Display selected transactions using TransactionItem -->
    <div v-if="hasTransactions" class="selected-transactions">
      <div
        v-for="(transaction, index) in message.transactions"
        :key="getTransactionId(transaction)"
        class="selected-transaction-row"
      >
        <div
          v-if="isEditable && index === 0"
          class="transaction-edit-icon"
          :style="actionIconStyle"
          @click="handleEdit(transaction)"
        >
          <EditPencil />
        </div>
        <div
          v-else-if="isEditable && index > 0"
          class="transaction-edit-icon transaction-edit-icon--placeholder"
          aria-hidden="true"
        />
        <TransactionItem
          :transaction="transaction"
          :transaction-type="message.transactionType"
          :show-checkbox="false"
          :editable="isEditable && index === 0"
          side="user"
        />
      </div>
    </div>
    <!-- "Transaction not listed": bubble shows summary text (prepend has pencil when editable) -->
    <span v-else class="selected-transaction-not-listed">{{ summaryMessage }}</span>
  </ChatMessageWrapper>
</template>

<style scoped lang="scss">
.selected-transactions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.selected-transaction-row {
  display: flex;
  align-items: center;
}

.transaction-edit-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: color 0.2s ease;
  padding: 12px;

  &:hover {
    opacity: 0.7;
  }

  &--placeholder {
    cursor: default;
    visibility: hidden;
  }
}

.selected-transaction-not-listed {
  padding: 12px 0;
}
</style>
